import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import PilotageModal from "./PilotageModal";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { previewMonthlyObjective, proposeMonthlyDistribution, updateObjective } from "@/lib/pilotageApi";
import { MONTH_NAMES, VISIBLE_MONTHLY_FIELDS, COMMON_MONTHLY_FIELDS, commonMonthlyDraft, parseCommonMonthlyDraft, RULE_LABELS, PLAN_LABELS, planTone, monthlyDraft, parseMonthlyDraft, formatMonthlyValue, fillEmptyMonthsFrom } from "@/lib/monthlyObjectives";

export default function MonthlyObjectivesEditor({objective, onSaved}) {
  const errorRef = useRef(null);
  const [open, setOpen] = useState(() => Boolean(objective?.id) && new URLSearchParams(window.location.search).get("view") === "monthly");
  const [month, setMonth] = useState(() => {
    const value = Number(new URLSearchParams(window.location.search).get("month"));
    return Number.isInteger(value) && value >= 1 && value <= 12 ? String(value).padStart(2, "0") : "01";
  });
  const [draft, setDraft] = useState(() => monthlyDraft(objective?.monthly_targets));
  const [common, setCommon] = useState(() => commonMonthlyDraft(objective));
  const [preview, setPreview] = useState(objective || null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [proposing, setProposing] = useState(false);
  const annual = useMemo(() => objective?.annual_targets || objective?.targets || {}, [objective?.annual_targets, objective?.targets]);

  useEffect(() => {
    if (!open) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get("view") === "monthly") {
      params.delete("view");
      window.history.replaceState(null, "", `${window.location.pathname}?${params}${window.location.hash}`);
    }
    let ignore = false;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const result = await previewMonthlyObjective({annual_targets: annual, monthly_targets: parseMonthlyDraft(draft), monthly_defaults: parseCommonMonthlyDraft(common)});
        if (!ignore) { setPreview(result); setError(""); }
      } catch (requestError) {
        if (!ignore) setError(getApiErrorMessage(requestError, requestError.message || "Impossible de vérifier les totaux."));
      } finally { if (!ignore) setChecking(false); }
    }, 250);
    return () => { ignore = true; clearTimeout(timer); };
  }, [open, draft, annual, common]);

  useEffect(() => {
    if (!open) return undefined;
    const field = new URLSearchParams(window.location.search).get("field");
    if (!VISIBLE_MONTHLY_FIELDS.some(item => item.key === field)) return undefined;
    const frame = requestAnimationFrame(() => document.getElementById(`monthly-objective-${field}`)?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const launch = () => {
    setDraft(monthlyDraft(objective?.monthly_targets));
    setCommon(commonMonthlyDraft(objective));
    setPreview(objective);
    setError("");
    setOpen(true);
  };
  const propose = async () => {
    setProposing(true); setError("");
    try {
      const result = await proposeMonthlyDistribution(objective.id);
      // Common rates stay inherited; a distribution must not create 12 overrides.
      const proposed = monthlyDraft(result.monthly_targets);
      for (const values of Object.values(proposed)) {
        for (const field of COMMON_MONTHLY_FIELDS) values[field.key] = "";
      }
      setDraft(proposed);
    }
    catch (requestError) { setError(getApiErrorMessage(requestError, "Impossible de proposer une répartition.")); }
    finally { setProposing(false); }
  };
  const save = async event => {
    event.preventDefault(); setSaving(true); setError("");
    try {
      const result = await updateObjective(objective.id, {monthly_targets: parseMonthlyDraft(draft), monthly_defaults: parseCommonMonthlyDraft(common)});
      setOpen(false);
      await onSaved?.(result);
      toast.success("Objectifs mensuels enregistrés");
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, requestError.message || "Impossible d’enregistrer les mois."));
      requestAnimationFrame(() => errorRef.current?.focus());
    }
    finally { setSaving(false); }
  };
  const status = preview?.reconciliation?.status || "ANNUAL_ONLY";
  const hasAnnual = Object.values(annual).some(value => value !== null && value !== undefined && Number.isFinite(Number(value)));
  const hasDraft = Object.values(draft).some(values => Object.values(values).some(value => String(value).trim() !== ""));
  return <>
    <Button type="button" variant="outline" onClick={launch} disabled={!objective?.id} className="h-11 border-white/10 text-white" data-testid="monthly-objectives-edit">Objectifs par mois</Button>
    <PilotageModal open={open} onOpenChange={value => {if (!saving) setOpen(value);}} title={`Objectifs par mois — ${objective?.year || ""} — ${objective?.name || ""}`}
      description={objective?.planning_source === "MONTHLY" ? "Le mensuel est actif : une saisie complète actualise l’annuel. Si elle devient incomplète, le dernier annuel valide est conservé." : "Les mois sont enregistrés séparément. Activez le mensuel dans Référence du pilotage pour remplacer l’annuel après contrôle."} onSubmit={save} isSubmitting={saving}>
      {error && <p ref={errorRef} tabIndex={-1} role="alert" className="rounded-lg border border-red-400/30 p-3 text-sm text-red-300">{error}</p>}
      <div className={`rounded-xl border p-3 text-sm ${planTone(status)}`} role="status">
        <span aria-hidden="true">{status === "MISMATCH" ? "● " : ""}</span>{checking ? "Contrôle des totaux…" : PLAN_LABELS[status] || status}
        <p className="mt-1 text-xs">Enregistrement autorisé même si les totaux diffèrent. Le choix de référence et le remplacement de l’annuel se font dans la page Objectifs.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" onClick={propose} disabled={hasDraft || !hasAnnual || proposing || saving} className="min-h-11 whitespace-normal border-white/10 text-slate-200">{proposing ? "Calcul de la proposition…" : "Proposer une répartition de l’annuel"}</Button>
        <p className="text-xs text-slate-400">Proposition modifiable depuis l’annuel, lorsque les mois sont vides. Sans annuel, commencez par un mois puis copiez-le vers les cases vides. Rien n’est écrit avant « Enregistrer ».</p>
      </div>
      <fieldset className="space-y-3 rounded-xl border border-white/10 p-3">
        <legend className="px-1 text-sm font-semibold text-white">TJM et marge pour toute l’année</legend>
        <p className="text-xs text-slate-400">Facultatifs. Ces valeurs s’appliquent à tous les mois sans valeur spécifique. Modifiez un mois ci-dessous pour faire une exception. La référence annuelle reste inchangée.</p>
        <div className="grid gap-3 sm:grid-cols-2">{COMMON_MONTHLY_FIELDS.map(field => <label key={field.key} className="text-sm text-slate-300">{field.label} — toute l’année ({field.unit})
          <input aria-label={`${field.label} — toute l’année`} type="text" inputMode="decimal" value={common[field.key] ?? ""} disabled={saving} placeholder="Facultatif"
            onChange={event => setCommon(current => ({...current, [field.key]: event.target.value}))} className="mt-1 h-11 w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-white" />
        </label>)}</div>
      </fieldset>
      <div role="group" aria-label="Mois à renseigner" className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {MONTH_NAMES.map((name, index) => {const key = String(index + 1).padStart(2, "0"); return <button type="button" key={key} aria-pressed={month === key} onClick={() => setMonth(key)} className={`min-h-11 rounded-lg border px-2 text-sm ${month === key ? "border-lenvia-blue bg-lenvia-blue/10 text-white" : "border-white/10 text-slate-300"}`}>{name}</button>;})}
      </div>
      <h3 className="text-lg font-semibold text-white">{MONTH_NAMES[Number(month) - 1]} {objective?.year}</h3>
      <p className="text-xs text-slate-400">Une case vide reste non renseignée. Zéro est une cible explicite. Les ETP mensuels utilisent le forfait de 18 jours.</p>
      <Button type="button" variant="outline" className="min-h-11 whitespace-normal border-white/10 text-slate-200" disabled={saving || !Object.values(draft[month] || {}).some(value => String(value).trim() !== "")}
        onClick={() => setDraft(current => fillEmptyMonthsFrom(current, month))}>Copier ce mois vers les cases vides</Button>
      <p className="text-xs text-slate-400">Complète les autres mois dans ce brouillon, sans remplacer les valeurs déjà saisies, y compris zéro.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {VISIBLE_MONTHLY_FIELDS.map(field => <label key={field.key} className="flex min-w-0 flex-col gap-1 text-sm text-slate-300">
          <span>{field.label}{field.unit ? ` (${field.unit})` : ""}</span>
          <input id={`monthly-objective-${field.key}`} aria-label={field.label} type="text" inputMode={field.signed ? "text" : "decimal"} value={draft[month]?.[field.key] ?? ""} placeholder={field.key in common && common[field.key] !== "" ? `Commun : ${common[field.key]}` : "Non renseigné"} disabled={saving}
            onChange={event => {const value = event.target.value; setDraft(current => ({...current, [month]: {...current[month], [field.key]: value}}));}}
            className="h-11 min-w-0 rounded-lg border border-white/10 bg-slate-900 px-3 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-lenvia-blue" />
          {field.key in common && <span className="text-xs text-slate-400">Vide : utilise la valeur commune. Une valeur saisie remplace celle-ci pour ce mois.</span>}
          {field.signed && <span className="text-xs text-slate-400">Une perte peut être saisie avec le signe moins, par exemple −1 500.</span>}
          {field.hint && <span className="text-xs text-slate-400">{field.hint}</span>}
        </label>)}
      </div>

      <details className="rounded-xl border border-white/10 p-3 text-slate-300">
        <summary className="min-h-11 cursor-pointer text-sm font-semibold">Comparer annuel et mois</summary>
        <div className="space-y-3">
          {VISIBLE_MONTHLY_FIELDS.map(field => {const item = preview?.reconciliation?.metrics?.[field.key]; return <div key={field.key} className="border-t border-white/10 pt-3 text-sm">
            <p className="font-semibold">{field.label}</p>
            <p className="text-xs text-slate-400">{RULE_LABELS[item?.rule]}</p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1"><span>Référence annuelle : {formatMonthlyValue(item?.annual, field)}</span><span>Mois : {formatMonthlyValue(item?.monthly_total, field)}</span></div>
            {item?.status === "MISMATCH" && <p className="mt-1 text-red-300">● Écart : {formatMonthlyValue(item.delta, field)} — non bloquant</p>}
            {item?.status === "UNAVAILABLE" && <p className="mt-1 text-amber-200">Consolidation non calculable : aucun poids de CA ou d’ETP positif.</p>}
            {item?.status === "INCOMPLETE" && <p className="mt-1 text-amber-200">{item.rule === "PERIOD_END" ? "Valeur de décembre à renseigner" : item.rule.startsWith("WEIGHTED") && item.entered === 12 ? "Renseignez aussi les ETP (TJM) ou le CA (marge) pour pondérer les 12 mois." : `${item.entered}/12 mois renseignés ; consolidation partielle`}</p>}
          </div>;})}
        </div>
      </details>
    </PilotageModal>
  </>;
}
